// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Loading from 'react-loading';
import { toast } from 'react-toastify';
import { withRouter } from 'react-router-dom';
import Scrollbars from 'react-custom-scrollbars';

import * as XLSX from 'xlsx';
import CSV from 'comma-separated-values';

import onPage from '../../utilities/onPage';
import api from '../../api';

// Styles
import '../../styles/format/spreadsheet.css';

class Spreadsheet extends React.Component {

    state = {
        sheets: [],
        activeSheet: 0,
        matches: {},
        loading: true
    }

    // API URL
    url = '/document'

    componentDidMount = async () => {
        await this.loadFile(this.props.match.params.documentId);

        if (this.props.match.params.query && this.props.match.params.query.length > 2)
            this.props.search(this.props.match.params.query.replace(/\|/g, '/'));
    }

    componentWillReceiveProps = async (next) => {
        // Reload (correct) document if updated
        if (next.match.params.documentId !== this.props.match.params.documentId)
            await this.loadFile(next.match.params.documentId);

        // Search passed through URL
        if (next.match.params.query !== this.props.match.params.query && next.match.params.query.length > 2)
            this.props.search(next.match.params.query);

        // Search passed through props
        if (next.query !== this.props.query)
            this.search(next.query);
    }

    getAccessHandler = () => this.props.accessHandler ? this.props.accessHandler : async call => await call();
    getAccessAuth = () => this.props.auth ? this.props.auth : "";

    search = (query="") => {
        if (!query || query.length < 3) return this.setState({ matches: {} });
        if (query[0] === "*") query = query.substring(1);
        query = query.toLowerCase();
        let matches = {};
        for (let s=0; s<this.state.sheets.length; s++) {
            const sheet = this.state.sheets[s];
            // Active sheet search
            if (this.state.activeSheet === s) {
                for (let r=0; r<sheet.nRows; r++) {
                    for (let c=0; c<sheet.nCols; c++) {
                        const cell = sheet.data[r][c].toLowerCase();
                        if (cell.includes(query)) {
                            // Found a match - let's highlight it
                            const start = cell.indexOf(query);
                            matches[this.getCellName(r,c)] = {
                                start,
                                stop: start + query.length
                            };
                        }
                    }
                }
            }

            else {
                // Just highlight other sheet results
                let content = sheet.name + "\n\n";
                for (const row of sheet.data) {
                    for (const cell of row)
                        content += cell.toLowerCase() + "\t";
                    content += "\n";
                }
                if (content.includes(query))
                    matches["!Sheet." + String(s+1)] = true;
            }
        }
        this.setState({ matches });
    }

    loadFile = async (documentId, auth=null) => {
        this.setState({ loading: true });
        if (!auth)
            auth = this.getAccessAuth();

        const res = await this.getAccessHandler()(async () => await api.get(this.url + '/get?docId=' + documentId + '&presigned=true' + auth));

        if (!res || !res.doc) return;

        let doc = { ...res.doc };

        if (!doc.viewed || !doc.viewed[String(this.props.user._id)])
            await this.props.viewDoc(doc);

        // Load duplicates
        if (auth === "") {
            const res2 = await api.get('/document/duplicates?docId=' + documentId, {}, true);
            if (res2 && res2.docs)
                doc.duplicates = [ ...res2.docs ];
        }

        await this.props.setDocument({
            ...doc,
            jobs: this.props.document?.jobs ?? [],
            duplicates: this.props.document?.duplicates ?? []   
        });

        // Download spreadsheet file to load on the browser
        const res3 = await api.download(doc.downloadUrl, {}, {}, true);
        if (!res3) return toast.error("Unable to download spreadsheet file");

        const raw = await (new Blob([ res3 ])).arrayBuffer();
        const sheets = [];

        // Microsoft Excel
        if (["xls", "xlsx"].includes(doc.format)) {
            const workbook = XLSX.read(raw, { type: 'binary' });
            for (const name of workbook.SheetNames) {
                const sheet = workbook.Sheets[name];
                const data = new CSV(XLSX.utils.sheet_to_csv(sheet)).parse();
                sheets.push({
                    name,
                    data,
                    nRows: data.length,
                    nCols: Math.max(...data.map(r => r.length))
                });
            }
        }

        // CSV file
        else if (["csv"].includes(doc.format)) {
            const data = new CSV(String.fromCharCode.apply(null, new Uint8Array(raw))).parse();
            sheets.push({
                name: doc.name,
                data,
                nRows: data.length,
                nCols: Math.max(...data.map(r => r.length))
            });
        }

        // OpenDocument Spreadsheet
        else if (["ods"].includes(doc.format)) {
            // TODO
        }

        this.setState({
            sheets,
            activeSheet: 0,
            loading: false
        });

        // Re-run search
        if (this.props.query && this.props.query.length > 3)
            this.search(this.props.query);
    }

    getColName = (i) => {
        const alpha = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const q = Math.floor(i/26);
        if (q > 0)
            return this.getColName(q-1) + alpha[i%26];
        return alpha[i%26];
    }

    getCellName = (r,c) => this.getColName(c) + String(r+1);

    getColHeader = (nCols) => {
        let header = [];
        for (let i=0; i<nCols; i++)
            header.push(this.getColName(i));
        return header;
    }

    backToStack = () => {
        this.props.search("");
        let query = this.props.match.params.query;
        const { field, value } = this.props.match.params;

        let path = window.location.href.includes('/field/') && field && value ? '/field/' + field + '/' + value : '/stack/' + this.props.match.params.stackId;

        const params = new URLSearchParams(this.props.location.search);
        const from = params.get('from');

        if (from === "root")
            path = "";

        else if (from === "field")
            path = '/field/' + params.get('field') + '/' + params.get('value');

        else if (from === "archives")
            return window.location.href = "/archives";

        else if (from)
            path = '/stack/' + from;

        if (query && query.length > 2) {
            if (query[0] === '*')
                query = query.substring(1);

            path += "/search/" + query + this.props.location.search;
        }

        this.props.history.push(path);
    }

    render = () => {
        const sheet = this.state.sheets[this.state.activeSheet];
        const pub = onPage(["public"]);

        return (<div className={pub ? "document-modal-public" : ("document-modal"  + (this.props.hideMeta ? " document-modal-nometa" : ""))} ref="document">

            <div className="document-modal-shadow" />

            {pub ? "" : <img
                className="document-close"
                src="https://static.stax.ai/images/icons/close.png"
                alt="x"
                title="Back to stack"
                onClick={this.backToStack}
            />}
            
            {this.state.loading || !sheet ? <div className="sheet-loading">
                <Loading
                    type="bubbles"
                    color="var(--color-primary)"
                    height={80}
                    width={80}
                />
            </div> : <>

                {/* List of sheets */}
                <div className={"sheets-container" + (pub ? " sheets-container-public" : "")}>
                    {this.state.sheets.map((sheet,idx) => <div
                        key={idx}
                        className={"sheets-sheet-title" + (this.state.activeSheet === idx ? " sheets-sheet-title-active" : "") + (this.state.matches["!Sheet." + String(idx+1)] ? " sheets-sheet-title-match" : "")}
                        title={sheet.name}
                        onClick={async () => {
                            await this.setState({ activeSheet: idx });
                            setImmediate(() => {
                                if (this.props.query && this.props.query.length > 2)
                                    this.search(this.props.query);
                            })
                        }}
                    >{sheet.name}</div>)}
                </div>

                <div className={"data-container" + (pub ? " data-container-public" : "")}>
                    <Scrollbars
                        renderTrackVertical={props => <div className="scrollbar-track-v" {...props} />}
                        renderTrackHorizontal={props => <div className="scrollbar-track-h" {...props} />}
                        renderThumbVertical={props => <div className="scrollbar-thumb-v" {...props} />}
                        renderThumbHorizontal={props => <div className="scrollbar-thumb-h" {...props} />}
                    >
                        <table className="data-table">
                            {/* Row headers */}
                            <tr className="data-table-col-header">
                                <th className="data-table-top-left"></th>
                                {this.getColHeader(sheet.nCols).map((h,idx) => <th
                                    key={idx}
                                    className={"data-table-col-header-main" + (this.state.activeCol === idx ? " data-table-header-active" : "")}
                                >{h}</th>)}
                            </tr>

                            {sheet.data.map((row,r) => <tr
                                key={r}
                                className="data-table-row"
                            >
                                <td
                                    className={"data-table-row-header" + (this.state.activeRow === r ? " data-table-header-active" : "")}
                                >{r}</td>
                                {row.map((cell,c) => {
                                    if (!cell) cell = "";
                                    const match = this.state.matches[this.getCellName(r,c)];
                                    const start = match ? cell.substring(0, match.start) : cell;
                                    const highlight = match ? cell.substring(match.start, match.stop) : "";
                                    const stop = match ? cell.substring(match.stop) : "";
                                    return <td
                                        key={c}
                                        className="data-table-cell"
                                    >
                                        <div
                                            className={"data-table-cell-value" + (this.state.activeRow === r && this.state.activeCol === c ? " data-table-cell-active" : "")}
                                            onClick={() => {
                                                if (this.state.activeRow === r && this.state.activeCol === c)
                                                    this.setState({ activeRow: -1, activeCol: -1 });
                                                else
                                                    this.setState({ activeRow: r, activeCol: c });
                                            }}
                                        >
                                            {start}
                                            <span className="data-table-cell-highlight">{highlight}</span>
                                            {stop}
                                        </div>
                                    </td>;
                                })}
                            </tr>)}
                        </table>
                    </Scrollbars>
                </div>
            </>}

        </div>);
    };

}

export default withRouter(Spreadsheet);